import { parsePlanDoc } from '@db/Plans/Common';
import { createNotFoundAppError, isPermissionDeniedError } from '@helpers/Errors';
import { MAX_OVERLAP_DISTANCE_KM, getCoordinateBounds } from '@helpers/Geography';
import { uniqueBy } from '@helpers/Parsers';
import db, { getDocsIn } from '@helpers/firebase/Firestore';
import { firestore } from '@helpers/firebase/Initialize';
import { Plan } from '@types';

const NEARBY_PLANS_LIMIT = 100;

export type PlanMapBounds = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

export async function getPlan(planId: string) {
  try {
    const docSnap = await db.getDoc(db.doc(firestore, 'plans', planId));
    if (docSnap.exists()) {
      return parsePlanDoc(docSnap);
    }
  } catch (error) {
    // Firestore returns permission-denied for unreadable docs. Keep hidden and
    // missing plans indistinguishable to callers.
    if (!isPermissionDeniedError(error)) throw error;
  }

  throw createNotFoundAppError(new Error('Plan does not exist. planId: ' + planId));
}

export async function getPlansIn(planIds: string[]) {
  const docs = await getDocsIn('plans', db.documentId(), 'in', planIds);
  return docs.map((doc) => parsePlanDoc(doc));
}

export async function getUserPlans(userId: string, constraints: db.QueryConstraint[] = []) {
  const q = db.query(
    db.collection(firestore, 'plans'),
    db.where('userId', '==', userId),
    ...constraints
  );
  const querySnapshot = await db.getDocs(q);
  return querySnapshot.docs.map((doc) => parsePlanDoc(doc));
}

export async function getUserJoinedPlans(userId: string, constraints: db.QueryConstraint[] = []) {
  const q = db.query(
    db.collection(firestore, 'plans'),
    db.where('userIds', 'array-contains', userId),
    ...constraints
  );
  const querySnapshot = await db.getDocs(q);
  return querySnapshot.docs.map((doc) => parsePlanDoc(doc));
}

export async function getUsersJoinedPlans(
  userIds: string[],
  constraints: db.QueryConstraint[] = []
) {
  const docs = await getDocsIn('plans', 'userIds', 'array-contains-any', userIds, constraints);
  const plans = docs.map((doc) => parsePlanDoc(doc));
  return uniqueBy(plans, 'planId');
}

export async function getUsersPlans(userIds: string[], constraints: db.QueryConstraint[] = []) {
  const docs = await getDocsIn('plans', 'userId', 'in', userIds, constraints);
  return docs.map((doc) => parsePlanDoc(doc));
}

export async function getUsersPlansInMapBounds(
  userIds: string[],
  bounds: PlanMapBounds,
  constraints: db.QueryConstraint[] = []
) {
  return getPlansInMapBounds('userId', 'in', userIds, bounds, constraints);
}

export async function getUsersJoinedPlansInMapBounds(
  userIds: string[],
  bounds: PlanMapBounds,
  constraints: db.QueryConstraint[] = []
) {
  return getPlansInMapBounds('userIds', 'array-contains-any', userIds, bounds, constraints);
}

export async function getGroupPlans(
  groupId: string,
  constraints: db.QueryConstraint[] = []
): Promise<Plan[]> {
  const q = db.query(
    db.collection(firestore, 'plans'),
    db.where('groupIds', 'array-contains', groupId),
    ...constraints
  );
  const snapshot = await db.getDocs(q);
  return snapshot.docs.map((doc) => parsePlanDoc(doc));
}

export async function getGroupsPlans(
  groupIds: string[],
  constraints: db.QueryConstraint[] = []
): Promise<Plan[]> {
  const docs = await getDocsIn('plans', 'groupIds', 'array-contains-any', groupIds, constraints);
  const plans = docs.map((doc) => parsePlanDoc(doc));
  // dedup data in case a plan has groupIds across chunks
  return uniqueBy(plans, 'planId');
}

export async function getGroupsPlansInMapBounds(
  groupIds: string[],
  bounds: PlanMapBounds,
  constraints: db.QueryConstraint[] = []
) {
  return getPlansInMapBounds('groupIds', 'array-contains-any', groupIds, bounds, constraints);
}

export async function getNearbyPlansForLocation(
  latitude: number,
  longitude: number,
  followingUserIds: Set<string>,
  groupIds: Set<string>
) {
  const center = { latitude, longitude };
  const bounds = getCoordinateBounds(center, MAX_OVERLAP_DISTANCE_KM);
  const constraints = [db.orderBy('createdAt', 'desc'), db.limit(NEARBY_PLANS_LIMIT)];
  const [followingPlans, groupsPlans] = await Promise.all([
    getUsersPlansInMapBounds([...followingUserIds.values()], bounds, constraints),
    getGroupsPlansInMapBounds([...groupIds.values()], bounds, constraints),
  ]);

  return uniqueBy([...followingPlans, ...groupsPlans], 'planId');
}

async function getPlansInMapBounds(
  fieldPath: string,
  opStr: db.WhereFilterOp,
  items: string[],
  bounds: PlanMapBounds,
  constraints: db.QueryConstraint[] = []
) {
  if (items.length === 0) return [];

  const { minLatitude, maxLatitude, minLongitude, maxLongitude } = getPlanMapBoundsLimits(bounds);
  const docs = await getDocsIn('plans', fieldPath, opStr, items, [
    ...constraints,
    db.where('location.latitude', '>=', minLatitude),
    db.where('location.latitude', '<=', maxLatitude),
    db.where('location.longitude', '>=', minLongitude),
    db.where('location.longitude', '<=', maxLongitude),
  ]);
  const plans = docs.map((doc) => parsePlanDoc(doc));
  return uniqueBy(plans, 'planId');
}

function getPlanMapBoundsLimits(bounds: PlanMapBounds) {
  const minLatitude = bounds.latitude - bounds.latitudeDelta / 2;
  const maxLatitude = bounds.latitude + bounds.latitudeDelta / 2;
  const minLongitude = bounds.longitude - bounds.longitudeDelta / 2;
  const maxLongitude = bounds.longitude + bounds.longitudeDelta / 2;

  return { maxLatitude, maxLongitude, minLatitude, minLongitude };
}
